import * as React from 'react';

import { SlDropdown, SlMenu } from '@shoelace-style/shoelace/dist/react';
import { TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useAtomValue } from 'jotai';

import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { RoleActionMenuItem } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/RoleActionMenuItem';
import { RoleMenuTriggerButton } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/RoleMenuTriggerButton';
import { roleNamesAndDescriptions } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/roleNamesAndDescriptions';

import { useHandleChangeToAdmin, useHandleChangeToMember } from './hooks';

interface RoleActionMenuOwnerProps {
  /**
   * The public ID of the target member.
   */
  targetPublicId: string;
  /**
   * The role of the target member.
   */
  targetMemberRole: TeamRole;
}

export function RoleActionMenuOwner({
  targetPublicId,
  targetMemberRole,
}: RoleActionMenuOwnerProps) {
  // Get the ID of the selected team
  const teamId = useAtomValue(selectedTeamIdAtom);

  // Hook to handle the change of a team member's role to Admin
  const { handleChangeToAdmin } = useHandleChangeToAdmin({
    onError: () => toast.critical(translate('An error has occurred')),
    onSuccess: () => toast(translate('Done')),
    teamId: teamId,
    targetPublicId: targetPublicId,
    targetTeamRole: targetMemberRole,
  });

  // Hook to handle the change of a team member's role to Member
  const { handleChangeToMember } = useHandleChangeToMember({
    onError: () => toast.critical(translate('An error has occurred')),
    onSuccess: () => toast(translate('Done')),
    teamId: teamId,
    targetPublicId,
    targetMemberRole,
  });

  return (
    <SlDropdown>
      <RoleMenuTriggerButton>
        {roleNamesAndDescriptions[targetMemberRole].title}
      </RoleMenuTriggerButton>
      <SlMenu className="action-menu">
        {/* Menu item to change the role to Member */}
        <RoleActionMenuItem
          menuItemTeamRole={TeamRole.Member}
          targetMemberRole={targetMemberRole}
          onClick={handleChangeToMember}
        />
        {/* Menu item to change the role to Admin */}
        <RoleActionMenuItem
          menuItemTeamRole={TeamRole.Admin}
          targetMemberRole={targetMemberRole}
          onClick={handleChangeToAdmin}
        />
      </SlMenu>
    </SlDropdown>
  );
}
